import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { en } from './locales/en';
import { ru } from './locales/ru';

/**
 * i18n from day one.
 *
 * WalletFront hardcoded Russian into JSX — and into the transport layer — so
 * adding English later would mean touching every file. Novix never puts a
 * user-visible string anywhere but these resource files. When a screen is ported
 * from WalletFront, its strings are extracted at porting time, not "later".
 */

export const SUPPORTED_LANGUAGES = ['ru', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'ru';

export const LANGUAGE_STORAGE_KEY = 'novix.lang';

export const resources = {
  ru: { translation: ru },
  en: { translation: en }
} as const;

/**
 * The language comes from the user's OWN choice or from the default. Never from
 * the browser.
 *
 * `i18next-browser-languagedetector` used to sit in front of this with
 * `order: ['localStorage', 'navigator']`. `navigator` meant that any visitor
 * whose browser reported `en-*` — which includes most default installs outside
 * Russia and every headless/CI browser — was served English on a
 * Russian-facing product, overriding the `ru` default that was written three
 * lines below it. The detector is gone: storage is read directly here, and if
 * it holds nothing the answer is `ru`.
 */
function readStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored !== null && (SUPPORTED_LANGUAGES as readonly string[]).includes(stored)) {
      return stored as Language;
    }
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_LANGUAGE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: readStoredLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: {
    // React escapes for us; double-escaping mangles currency symbols.
    escapeValue: false
  },
  returnNull: false
});

export function setLanguage(language: Language): void {
  void i18n.changeLanguage(language);
  document.documentElement.lang = language;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    /* storage unavailable — the choice still applies for this session */
  }
}

/** BCP-47 locale for Intl, derived from the active language. */
export function currentLocale(): string {
  return i18n.language === 'en' ? 'en-US' : 'ru-RU';
}

export default i18n;
